import React, { useEffect, useState } from 'react';
import { Typography, Box, Stack, CircularProgress, Button } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { ISwitchWithCustomText } from '../Switch/SwitchWithCustomText';
import { IUserConfiguration } from '../../redux/configuration/types';
import { IPaymentCard } from '../../types/global/generic.types';
import PaymentCard from '../PaymentCard/PaymentCard';
import { userConfigurationSelector } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import AddIcon from '../CustomSVGIcons/AddIcon';
import PaymentModal from '../Payment/PaymentModal';
import { PayStack } from '../Payment/Paystack';
import { getJWtDetails, showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { en_config } from '../../config';
import type { BillingPaymentProvider } from '../../utils/billingPaymentRailCopy';
import {
    billingAddCardButtonLabel,
    billingAddCardSubtitle,
    billingAddCardTitle,
    billingCardCheckNote,
} from '../../utils/billingPaymentRailCopy';
import { initializeFlutterwaveBillingCard } from '../../redux/payment/action';
import { ROUTES } from '../../types/global/routes.types';
import {
    OBSIDIAN,
    OBSIDIAN_BODY_FONT,
    OBSIDIAN_DISPLAY_FONT,
    ensureObsidianDashboardFonts,
} from '../../constants/obsidianDashboardTheme';
import MasterCard from '../CustomSVGIcons/MasterCard';
import VisaCard from '../CustomSVGIcons/VisaCard';

export interface ISettingsProps extends Omit<ISwitchWithCustomText, 'header' | 'body' | 'theme'> {
    onChange?: (payload: any) => void;
    setting?: IUserConfiguration;
    paymentMethods?: IPaymentCard[];
    onDeleteCard?: (card: IPaymentCard, isDefaultCard?: boolean) => void;
    onMakeDefault?: (card: IPaymentCard) => void;
    /** @deprecated Prefer `provider` */
    isNonUs?: boolean;
    publicKey: string;
    provider?: BillingPaymentProvider;
}

export function isExpired(month: string, year: string) {
    if (!month || !year) return false;
    const inputDate = dayjs(`${year}-${month}-01`).endOf('month');
    if (!inputDate.isValid()) return false;
    return inputDate.isBefore(dayjs());
}

const sectionShellSx = {
    bgcolor: OBSIDIAN.surfaceContainer,
    border: `1px solid ${OBSIDIAN.surfaceContainerHighest}`,
    borderRadius: '12px',
    p: { xs: 2, sm: 3, md: 4 },
    width: '100%',
    boxSizing: 'border-box' as const,
    position: 'relative' as const,
    overflow: 'hidden' as const,
};

const primaryCtaSx = {
    py: 1.5,
    px: 2.5,
    borderRadius: '999px',
    textTransform: 'none' as const,
    fontFamily: OBSIDIAN_BODY_FONT,
    fontWeight: 700,
    fontSize: 14,
    color: OBSIDIAN.onPrimaryFixed,
    bgcolor: OBSIDIAN.primary,
    boxShadow: 'none',
    '&:hover': { bgcolor: OBSIDIAN.primaryDim },
    '&.Mui-disabled': {
        bgcolor: OBSIDIAN.surfaceContainerHighest,
        color: OBSIDIAN.onSurfaceVariant,
    },
};

const AccountBilling: React.FC<ISettingsProps> = ({
    paymentMethods,
    onDeleteCard,
    onMakeDefault,
    isNonUs,
    publicKey,
    provider: providerProp,
}) => {
    const userSpecificConfiguration = useSelector(userConfigurationSelector);
    const [showDeleteDialogue, setShowDeleteDialogue] = useState(false);
    const [cardToDelete, setCardToDelete] = useState<IPaymentCard | null>(null);
    const [open, setOpen] = useState(false);
    const [flutterwaveOpening, setFlutterwaveOpening] = useState(false);
    const { user } = getJWtDetails();
    const dispatchPromise = useDispatchPromise();
    const provider: BillingPaymentProvider = providerProp ?? (isNonUs ? 'paystack' : 'stripe');
    const cards = (paymentMethods ?? []).filter(c => Boolean(c.authorization));
    const hasCards = cards.length > 0;
    const note = billingCardCheckNote(provider, {
        amountMinor: en_config.PAYSTACK_CHARGE,
        currency: 'NGN',
    });

    useEffect(() => {
        ensureObsidianDashboardFonts();
    }, []);

    const handleFlutterwaveAdd = async () => {
        setFlutterwaveOpening(true);
        try {
            const res = (await dispatchPromise(
                initializeFlutterwaveBillingCard({
                    callback_url: `${window.location.origin}${ROUTES.ACCOUNT_BILLING}`,
                }),
            )) as any;
            const payload = res?.data ?? res;
            const authUrl = payload?.authorization_url || payload?.payment_link;
            if (!authUrl) {
                showToastMessage('Could not start Flutterwave card check. Try again.', 'error');
                return;
            }
            window.location.href = authUrl;
        } catch {
            showToastMessage('Could not start Flutterwave card check. Try again.', 'error');
        } finally {
            setFlutterwaveOpening(false);
        }
    };

    const addLabel = flutterwaveOpening
        ? 'Opening Flutterwave…'
        : provider === 'stripe'
        ? 'Add new card'
        : billingAddCardButtonLabel(provider);

    const renderAddTrigger = (variant: 'link' | 'primary' = 'link') => {
        const isPrimary = variant === 'primary';
        const linkSx = {
            border: 'none',
            bgcolor: 'transparent',
            color: OBSIDIAN.primary,
            textTransform: 'none' as const,
            fontFamily: OBSIDIAN_BODY_FONT,
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '0.02em',
            px: 1,
            py: 0.5,
            minWidth: 0,
            '&:hover': {
                bgcolor: 'transparent',
                color: OBSIDIAN.onSurface,
            },
        };

        if (provider === 'paystack' && user) {
            return (
                <PayStack
                    email={user.email}
                    amount={en_config.PAYSTACK_CHARGE}
                    publicKey={publicKey}
                    isAddNewCardButton={false}
                    addButtonSx={
                        isPrimary
                            ? {
                                  ...primaryCtaSx,
                                  width: { xs: '100%', sm: 'auto' },
                              }
                            : linkSx
                    }
                    addButtonTextStyle={{
                        fontFamily: OBSIDIAN_BODY_FONT,
                        fontWeight: 700,
                        fontSize: 14,
                        color: isPrimary ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.primary,
                    }}
                    addButtonProps={{ variant: isPrimary ? 'contained' : 'text' }}
                />
            );
        }

        if (provider === 'flutterwave') {
            return (
                <Button
                    onClick={() => void handleFlutterwaveAdd()}
                    disabled={flutterwaveOpening}
                    startIcon={
                        flutterwaveOpening ? (
                            <CircularProgress size={16} sx={{ color: 'inherit' }} />
                        ) : (
                            <AddIcon className="billing-add-card-icon" height="18px" width="18px" fill="currentColor" />
                        )
                    }
                    sx={isPrimary ? { ...primaryCtaSx, width: { xs: '100%', sm: 'auto' } } : linkSx}>
                    {addLabel}
                </Button>
            );
        }

        return (
            <Button
                onClick={() => setOpen(true)}
                startIcon={<AddIcon className="billing-add-card-icon" height="18px" width="18px" fill="currentColor" />}
                sx={isPrimary ? { ...primaryCtaSx, width: { xs: '100%', sm: 'auto' } } : linkSx}>
                {addLabel}
            </Button>
        );
    };

    return (
        <>
            <PaymentModal onClose={() => setOpen(false)} open={open} amount={10} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 3,
                    width: '100%',
                    maxWidth: 960,
                    mx: 'auto',
                    p: { xs: 2, sm: 0 },
                }}>
                {cardToDelete && (
                    <DialogueSheet
                        onClose={() => {
                            setCardToDelete(null);
                            setShowDeleteDialogue(false);
                        }}
                        open={showDeleteDialogue}
                        onAgreeClicked={() => {
                            onDeleteCard?.(
                                cardToDelete,
                                userSpecificConfiguration?.default_payment_method === cardToDelete._id,
                            );
                            setShowDeleteDialogue(false);
                        }}
                        onDisagreeClick={() => {
                            setCardToDelete(null);
                            setShowDeleteDialogue(false);
                        }}
                        header="Delete Card?"
                        body="You are about to delete this card and this action cannot be reversed."
                    />
                )}

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    spacing={2}>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontWeight: 500,
                            color: OBSIDIAN.onSurfaceVariant,
                            lineHeight: 1.5,
                            maxWidth: '28rem',
                        }}>
                        Cards on file for subscriptions and billing. Default card is charged first.
                    </Typography>
                    {hasCards ? (
                        <Box
                            sx={{
                                alignSelf: { xs: 'stretch', sm: 'flex-start' },
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}>
                            {renderAddTrigger('link')}
                        </Box>
                    ) : null}
                </Stack>

                <Box component="section" sx={sectionShellSx}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={2}
                        sx={{ mb: hasCards ? 3 : 0, width: '100%' }}>
                        <Typography
                            sx={{
                                fontSize: { xs: '1.05rem', sm: '1.25rem' },
                                fontWeight: 700,
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                letterSpacing: '-0.02em',
                                color: OBSIDIAN.onSurface,
                            }}>
                            Payment Methods
                        </Typography>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                            <LockOutlinedIcon sx={{ fontSize: 14, color: OBSIDIAN.onSurfaceVariant }} />
                            <Typography
                                sx={{
                                    fontSize: '0.625rem',
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                    fontWeight: 700,
                                    color: OBSIDIAN.onSurfaceVariant,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.14em',
                                }}>
                                Encrypted
                            </Typography>
                        </Stack>
                    </Stack>

                    {hasCards ? (
                        <Stack spacing={1.5} sx={{ width: '100%' }}>
                            {cards.map(card => (
                                <PaymentCard
                                    key={card._id}
                                    card={card}
                                    variant="obsidian"
                                    isExpired={isExpired(card.authorization?.exp_month, card.authorization?.exp_year)}
                                    isDefault={userSpecificConfiguration?.default_payment_method === card._id}
                                    onSetDefault={() => onMakeDefault?.(card)}
                                    onDelete={() => {
                                        setCardToDelete(card);
                                        setShowDeleteDialogue(true);
                                    }}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                mt: 3,
                                py: { xs: 4, sm: 5 },
                                px: { xs: 2, sm: 3 },
                                borderRadius: '12px',
                                bgcolor: OBSIDIAN.surfaceContainerLow,
                                border: `1px dashed ${OBSIDIAN.surfaceContainerHighest}`,
                                textAlign: 'center',
                            }}>
                            <Stack alignItems="center" spacing={2}>
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: '14px',
                                        bgcolor: OBSIDIAN.surfaceContainerHighest,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <CreditCardOutlinedIcon sx={{ color: OBSIDIAN.primary, fontSize: 28 }} />
                                </Box>
                                <Stack direction="row" spacing={1} justifyContent="center">
                                    <MasterCard height="36px" width="36px" />
                                    <VisaCard height="36px" width="36px" />
                                </Stack>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                                            fontWeight: 700,
                                            fontSize: { xs: 18, sm: 20 },
                                            color: OBSIDIAN.onSurface,
                                            mb: 0.75,
                                        }}>
                                        {billingAddCardTitle(provider)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: OBSIDIAN_BODY_FONT,
                                            fontSize: 13,
                                            color: OBSIDIAN.onSurfaceVariant,
                                            maxWidth: 360,
                                            mx: 'auto',
                                            lineHeight: 1.5,
                                        }}>
                                        {billingAddCardSubtitle(provider)}
                                    </Typography>
                                </Box>
                                {renderAddTrigger('primary')}
                                {note ? (
                                    <Typography
                                        sx={{
                                            fontFamily: OBSIDIAN_BODY_FONT,
                                            fontSize: 11,
                                            color: OBSIDIAN.outline,
                                            maxWidth: 380,
                                            lineHeight: 1.45,
                                        }}>
                                        {note}
                                    </Typography>
                                ) : null}
                            </Stack>
                        </Box>
                    )}
                </Box>

                {hasCards && note ? (
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 11,
                            color: OBSIDIAN.outline,
                            lineHeight: 1.45,
                        }}>
                        {note}
                    </Typography>
                ) : null}
            </Box>
        </>
    );
};

export { AccountBilling };
